import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {BLOCKS, INLINES} from '@contentful/rich-text-types'
import type {Document} from '@contentful/rich-text-types'

import {Button, Heading, InlineLink, Label, Text} from '@primer/react-brand'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'
import {clsx} from 'clsx'

import Styles from './PlanCard.module.css'

interface CtaAnalytics {
  marketingAnalyticsEvent: Parameters<typeof getAnalyticsEvent>[0]
  snakeCaseOptions?: Parameters<typeof getAnalyticsEvent>[1]
}

export type PlanCardProps = {
  id?: string
  label: string
  title: string
  titleAs?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  description: string
  price: {
    value: number
    currency: string
    currencySymbol: string
    detailText: string
  }
  cta1: {
    label: string
    href: string
    analytics?: CtaAnalytics
  }
  cta2: {
    label: string
    href: string
    analytics?: CtaAnalytics
  }
  detailText: string | Document
}

const defaultProps: Partial<PlanCardProps> = {
  titleAs: 'h2',
}

const PlanCard: React.FC<PlanCardProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {label, title, titleAs, description, price, cta1, cta2, detailText} = initializedProps

  return (
    <div className={Styles['PlanCard']}>
      <Label className={clsx(Styles['PlanCard__label'])}>{label}</Label>

      <div className={Styles['PlanCard__content']}>
        <div className={Styles['PlanCard__titleBlock']}>
          <Heading size="4" as={titleAs}>
            {title}
          </Heading>

          <Text variant="muted">{description}</Text>
        </div>

        <div className={Styles['PlanCard__price']}>
          <div className={Styles['PlanCard__priceTag']}>
            <Text font="hubot-sans" size="700" weight="normal">
              {price.currencySymbol}
              {price.value}
            </Text>

            <Text variant="muted">{price.currency}</Text>
          </div>

          <Text variant="muted">{price.detailText}</Text>
        </div>

        <div className={Styles['PlanCard__ctaBlock']}>
          <div>
            <Button
              href={cta1.href}
              as="a"
              variant="primary"
              block
              {...(cta1.analytics
                ? getAnalyticsEvent(cta1.analytics.marketingAnalyticsEvent, cta1.analytics.snakeCaseOptions)
                : {})}
            >
              {cta1.label}
            </Button>

            <Button
              href={cta2.href}
              as="a"
              block
              {...(cta2.analytics
                ? getAnalyticsEvent(cta2.analytics.marketingAnalyticsEvent, cta2.analytics.snakeCaseOptions)
                : {})}
            >
              {cta2.label}
            </Button>
          </div>

          <Text size="100" variant="muted">
            {typeof detailText === 'string'
              ? detailText
              : documentToReactComponents(detailText, {
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (_, children) => children,
                    [INLINES.EMBEDDED_ENTRY]: node => {
                      if (node.data.target?.sys?.contentType?.sys?.id === 'link') {
                        const {text, href, openInNewTab} = node.data.target.fields
                        return (
                          <>
                            <br />
                            <InlineLink
                              href={href}
                              target={openInNewTab ? '_blank' : undefined}
                              style={{textDecoration: 'none'}}
                              {...getAnalyticsEvent({
                                action: text,
                                tag: 'hyperlink',
                                context: 'footnotes',
                                location: 'pricing',
                              })}
                            >
                              {text}
                            </InlineLink>
                          </>
                        )
                      }
                      return null
                    },
                  },
                })}
          </Text>
        </div>
      </div>
    </div>
  )
}

export default PlanCard
